/* eslint-disable jsx-a11y/click-events-have-key-events */
import axios from 'axios';
import cl from 'classnames';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Demo from './SkeletonDemo';
import Input from '../Input/Input';
import Button from '../Button/Button';
import ButtonGroup from '../Button/ButtonGroup';
import Constants from '../../Constants';
import { tr } from '../utils/locale';
import markerIcon from '../../images/marker.png';
import markerIconAlt from '../../images/marker-alt.png';
import { createWoosmapMap } from '../Map/drawOnMap';

const languages = [
    { value: 'fr', label: 'French' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'it', label: 'Italian' },
    { value: 'de', label: 'German' },
    { value: 'nl', label: 'Dutch' },
];

export default class W3WDemo extends Component {
    constructor(props) {
        super(props);
        const lang = (typeof window === 'object' && (navigator.language || navigator.userLanguage)) || 'gb';
        const defaultLang = languages.find((item) => lang.startsWith(item.value));
        this.state = {
            w3w: null,
            selectedAddress: null,
            error: null,
            input: '///walnuts.octopus.mount',
            language: (defaultLang || languages[1]).value,
            suggestions: null,
        };

        this.mounted = true;
        this.map = null;
        this.markers = [];
        this.viewport = null;
        this.baseRequestUrl = 'https://api.woosmap.com/what3words/';
        this.demoRef = React.createRef();
        this.overlay = null;
    }

    componentDidMount() {
        this.displayMap();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    requestW3W = (words) => {
        axios
            .get(`${this.baseRequestUrl}convert-to-address`, {
                params: this.getRequestparams({ words }),
            })
            .then((response) => {
                if (this.mounted) {
                    const selectedAddress = response.data.results.length > 0 ? response.data.results[0] : null;
                    this.setState(
                        {
                            w3w: response.data,
                            error: null,
                        },
                        () => this.selectAddress(selectedAddress)
                    );
                }
            })
            .catch((error) => {
                const errorResult = (error && error.response && error.response.data) || 'Unhandled error';
                this.setState({ error: errorResult });
            });
    };

    autosuggestW3W = () => {
        const { input } = this.state;
        axios
            .get(`${this.baseRequestUrl}autosuggest`, {
                params: this.getRequestparams({ input, 'clip-to-country': 'GB,FR' }),
            })
            .then((response) => {
                if (this.mounted) {
                    this.setState({
                        w3w: null,
                        suggestions: response.data,
                        error: null,
                    });
                }
            })
            .catch((error) => {
                const errorResult = (error && error.response && error.response.data) || 'Unhandled error';
                this.setState({ error: errorResult });
            });
    };

    getRequestparams = (input) => {
        const { language } = this.state;
        return {
            key: Constants.woosmapKey,
            language,
            ...input,
        };
    };

    selectAddress = (address) => {
        this.setState({ selectedAddress: address }, this.displayOnMap);
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    displayOnMap = () => {
        const { w3w, selectedAddress } = this.state;
        if (this.map) {
            if (this.markers.length > 0) {
                this.markers.forEach((marker) => marker.setMap(null));
                this.marker = [];
            }
            w3w.results.forEach((address) => {
                if (address.geometry && address.geometry.location) {
                    const marker = new window.woosmap.map.Marker({
                        icon: {
                            url: address === selectedAddress ? markerIconAlt : markerIcon,
                            scaledSize: { height: 46, width: 30 },
                        },
                        position: address.geometry.location,
                        map: this.map,
                    });
                    this.markers.push(marker);
                    marker.addListener('click', () => {
                        this.markers.forEach((_marker) => {
                            _marker.setIcon({
                                url: marker === _marker ? markerIconAlt : markerIcon,
                                scaledSize: { height: 46, width: 30 },
                            });
                        });
                        this.setState({ selectedAddress: address });
                    });

                    this.map.setCenter(address.geometry.location);
                    this.map.setZoom(18);
                }
            });
        }
    };

    renderAutocompleteList = () => {
        const { w3w, suggestions } = this.state;
        if (w3w) {
            const result = w3w.results.map((address) => {
                const { selectedAddress } = this.state;
                return (
                    <li key={address.public_id}>
                        <Button
                            type="link-flex"
                            label={address.formatted_address}
                            active={selectedAddress === address}
                            onClick={() => this.selectAddress(address)}
                        />
                    </li>
                );
            });
            if (result.length === 0) {
                return (
                    <div className="demo__results__content__action">
                        {suggestions && (
                            <Button
                                icon="arrow-left"
                                type="secondary"
                                size="mini"
                                label={tr('Back')}
                                onClick={() => this.setState({ w3w: null })}
                            />
                        )}
                        <div className="demo__empty">{tr('No address found for this location')}</div>
                    </div>
                );
            }
            return (
                <div className="demo__results__content__action">
                    {suggestions && (
                        <Button
                            icon="arrow-left"
                            type="secondary"
                            size="mini"
                            label={tr('Back')}
                            onClick={() => this.setState({ w3w: null })}
                        />
                    )}
                    <ul>{result}</ul>
                </div>
            );
        }
        if (suggestions) {
            const result = suggestions.suggestions.map((suggestion) => (
                <li key={`sugg_${suggestion.words}`}>
                    <button
                        type="button"
                        className="btn btn--link-flex btn--two-lines"
                        onClick={() => this.requestW3W(suggestion.words)}
                    >
                        <span>{suggestion.nearestPlace}</span>
                        <strong>{suggestion.words}</strong>
                    </button>
                </li>
            ));
            if (result.length === 0) {
                return <div className="demo__empty">{tr('No locality found')}</div>;
            }
            return <ul>{result}</ul>;
        }

        return <div className="demo__empty">{tr('No locality found')}</div>;
    };

    displayMap = () => {
        if (this.timeoutMap) {
            clearTimeout(this.timeoutMap);
        }

        if (window.woosmap && window.woosmap.map && this.demoRef.current) {
            window.woosmap.map.config.setApiKey(Constants.woosmapKey);
            this.map = createWoosmapMap(this.demoRef.current.getMap());
            const { input } = this.state;
            this.requestW3W(input);
        } else {
            this.timeoutMap = setTimeout(this.displayMap, 300);
        }
    };

    renderHeaderFilters = () => {
        const { input } = this.state;
        const { usecaseFilter } = this.props;
        const inputFilter = {
            label: tr('Type in '),
            component: (
                <Input
                    value={input}
                    autocomplete={false}
                    placeholder={tr('Type a locality name...')}
                    onChange={(e) => {
                        let { value } = e.target;
                        if (value.indexOf('///') !== 0) {
                            value = `///${value.replaceAll('/', '')}`;
                        }
                        this.setState({ input: value }, this.autosuggestW3W);
                    }}
                />
            ),
        };
        return [usecaseFilter, inputFilter];
    };

    renderFooterFilters = () => {
        const { language, input } = this.state;
        const filterCountries = {
            label: tr('Response language'),
            component: (
                <ButtonGroup className="language" isLight>
                    {languages.map((item) => (
                        <Button
                            size="small"
                            type="group"
                            key={item.value}
                            active={language === item.value}
                            onClick={() => {
                                this.setState({ language: item.value }, () => this.requestW3W(input));
                            }}
                        >
                            <span className={cl('flag', `flag-${item.value}`)} title={item.label} />
                        </Button>
                    ))}
                </ButtonGroup>
            ),
        };
        return [filterCountries];
    };

    render() {
        const { w3w, error } = this.state;
        const { noheader, headerLabels, subHeader } = this.props;
        return (
            <Demo
                id="w3w-demo"
                header={headerLabels}
                subHeader={subHeader}
                docLink="https://developers.woosmap.com/products/w3w/get-started/"
                className="demo--w3w"
                footerFilters={this.renderFooterFilters()}
                headerFilters={this.renderHeaderFilters()}
                request={this.requestUrl}
                noheader={noheader}
                params={this.getRequestparams()}
                referer="http://localhost/"
                response={error || w3w}
                result={this.renderAutocompleteList()}
                withMap
                ref={this.demoRef}
            />
        );
    }
}

W3WDemo.defaultProps = {
    noheader: false,
    headerLabels: {},
    usecaseFilter: null,
    subHeader: null,
};
W3WDemo.propTypes = {
    noheader: PropTypes.bool,
    headerLabels: PropTypes.object,
    usecaseFilter: PropTypes.object,
    subHeader: PropTypes.object,
};
